<template>
  <v-container
    v-if="useNewLoginScreen"
    fluid
    fill-height
    class="pa-0"
    :style="loginContainerStyle"
  >
    <v-row no-gutters class="fill-height d-flex align-center justify-center">
      <v-col cols="12" class="d-flex flex-column align-start" style="padding-left: 250px">
        <img
          v-if="showLoginImage1"
          :src="loginImage1Path"
          alt="Logo"
          class="mb-4"
          :height="loginImage1Height"
        />
        <v-card class="pa-5" max-width="400" outlined>
          <v-card-title class="headline primary--text d-flex">
            <div>
              <div class="headline">Hi, Welcome Back</div>
              <div class="subtitle-2 grey--text">Please sign in to continue</div>
            </div>

            <img
              :src="require('@/assets/LumoraLogoHorizontal.png')"
              alt="Logo"
              class="ml-10"
              height="32"
            />
          </v-card-title>

          <v-divider></v-divider>
          <v-card-text>
            <div v-if="!twoFactorMode && !forgotPassword" class="loginCredentials">
              <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                >Sign in with Username</v-card-subtitle
              >
              <v-form class="pa-1" @submit.prevent="login">
                <v-text-field solo label="Username" type="text" v-model="username"></v-text-field>
                <v-text-field
                  solo
                  label="Password"
                  type="password"
                  v-model="password"
                ></v-text-field>

                <div class="d-flex justify-end mt-2 mb-1">
                  <a href="#" @click.prevent="forgotPassword = true">Forgot Password?</a>
                </div>

                <v-divider></v-divider>

                <div class="mt-4">
                  <v-btn color="primary" type="submit" block depressed>
                    <v-icon left>mdi-lock</v-icon>
                    Login
                  </v-btn>
                </div>
              </v-form>
            </div>
            <div v-else-if="twoFactorMode" class="login2FA">
              <p>Please enter your unique code provided by your selected 2-Factor method.</p>
              <v-spacer></v-spacer>
              <div class="code-inputs">
                <input
                  v-for="(digit, index) in code"
                  :key="index"
                  ref="inputs"
                  type="text"
                  maxlength="1"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  v-model="code[index]"
                  @input="onInput(index)"
                  @keydown.backspace="onBackspace(index)"
                />
              </div>
              <v-container>
                <v-row class="mt-10">
                  <v-spacer></v-spacer>
                  <v-btn color="primary" @click="setTwoFactorCode" depressed>
                    <v-icon left>mdi-lock</v-icon>
                    Verify
                  </v-btn>
                </v-row>
              </v-container>
            </div>
            <div v-else class="pa-1">
              <div v-if="!emailSent">
                <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                  >Enter you email address below and we'll send you a unique 6 digit
                  code.</v-card-subtitle
                >
                <v-text-field solo label="Email" type="text" v-model="emailAddress"></v-text-field>

                <div class="mb-2">
                  <v-btn color="primary" @click="sendEmail" block depressed>
                    <v-icon left>mdi-send</v-icon>
                    Send Email
                  </v-btn>
                </div>

                <v-divider></v-divider>

                <div class="d-flex mt-2 mb-1">
                  <a href="#" @click="forgotPassword = false">Back to login</a>
                </div>
              </div>
              <div v-else-if="emailSent && !resetPassword">
                <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                  >Check your email for the unique 6-digit code and enter it below.</v-card-subtitle
                >
                <div class="code-inputs">
                  <input
                    v-for="(digit, index) in passwordResetCode"
                    :key="index"
                    ref="resetInputs"
                    type="text"
                    maxlength="1"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    v-model="passwordResetCode[index]"
                    @input="onInputs(index)"
                    @keydown.backspace="onBackspaces(index)"
                  />
                </div>
                <v-container>
                  <v-row class="mt-10">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="validateResetCode" depressed>
                      <v-icon left>mdi-lock</v-icon>
                      Verify
                    </v-btn>
                  </v-row>
                </v-container>
              </div>
              <div v-else>
                <v-card-title>
                  <span class="text-h6">Reset Password</span>
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="newPassword"
                    label="New Password"
                    type="password"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    dense
                  ></v-text-field>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="forgotPassword = false" color="primary" outlined>Cancel</v-btn>
                  <v-btn @click="savePassword" color="primary" depressed>Save</v-btn>
                </v-card-actions>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else fluid fill-height class="pa-0">
    <v-row no-gutters class="fill-height">
      <!-- Left Panel (Login Form Background) -->
      <v-col cols="12" md="7" class="left-panel d-flex align-center justify-center">
        <v-card class="pa-5" max-width="400" outlined>
          <v-card-title class="headline primary--text d-flex">
            <div>
              <div class="headline">Hi, Welcome Back</div>
              <div class="subtitle-2 grey--text">Please sign in to continue</div>
            </div>

            <img
              :src="require('@/assets/LumoraLogoHorizontal.png')"
              alt="Logo"
              class="ml-10"
              height="32"
            />
          </v-card-title>

          <v-divider></v-divider>
          <v-card-text>
            <div v-if="!twoFactorMode && !forgotPassword" class="loginCredentials">
              <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                >Sign in with Username</v-card-subtitle
              >
              <v-form class="pa-1" @submit.prevent="login">
                <v-text-field solo label="Username" type="text" v-model="username"></v-text-field>
                <v-text-field
                  solo
                  label="Password"
                  type="password"
                  v-model="password"
                ></v-text-field>

                <div class="d-flex justify-end mt-2 mb-1">
                  <a href="#" @click.prevent="forgotPassword = true">Forgot Password?</a>
                </div>

                <v-divider></v-divider>

                <div class="mt-4">
                  <v-btn color="primary" type="submit" block depressed>
                    <v-icon left>mdi-lock</v-icon>
                    Login
                  </v-btn>
                </div>
              </v-form>
            </div>
            <div v-else-if="twoFactorMode" class="login2FA">
              <p>Please enter your unique code provided by your selected 2-Factor method.</p>
              <v-spacer></v-spacer>
              <div class="code-inputs">
                <input
                  v-for="(digit, index) in code"
                  :key="index"
                  ref="inputs"
                  type="text"
                  maxlength="1"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  v-model="code[index]"
                  @input="onInput(index)"
                  @keydown.backspace="onBackspace(index)"
                />
              </div>
              <v-container>
                <v-row class="mt-10">
                  <v-spacer></v-spacer>
                  <v-btn color="primary" @click="setTwoFactorCode" depressed>
                    <v-icon left>mdi-lock</v-icon>
                    Verify
                  </v-btn>
                </v-row>
              </v-container>
            </div>
            <div v-else class="pa-1">
              <div v-if="!emailSent">
                <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                  >Enter you email address below and we'll send you a unique 6 digit
                  code.</v-card-subtitle
                >
                <v-text-field solo label="Email" type="text" v-model="emailAddress"></v-text-field>

                <div class="mb-2">
                  <v-btn color="primary" @click="sendEmail" block depressed>
                    <v-icon left>mdi-send</v-icon>
                    Send Email
                  </v-btn>
                </div>

                <v-divider></v-divider>

                <div class="d-flex mt-2 mb-1">
                  <a href="#" @click="forgotPassword = false">Back to login</a>
                </div>
              </div>
              <div v-else-if="emailSent && !resetPassword">
                <v-card-subtitle class="mb-1 d-flex align-center justify-center black--text"
                  >Check your email for the unique 6-digit code and enter it below.</v-card-subtitle
                >
                <div class="code-inputs">
                  <input
                    v-for="(digit, index) in passwordResetCode"
                    :key="index"
                    ref="resetInputs"
                    type="text"
                    maxlength="1"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    v-model="passwordResetCode[index]"
                    @input="onInputs(index)"
                    @keydown.backspace="onBackspaces(index)"
                  />
                </div>
                <v-container>
                  <v-row class="mt-10">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="validateResetCode" depressed>
                      <v-icon left>mdi-lock</v-icon>
                      Verify
                    </v-btn>
                  </v-row>
                </v-container>
              </div>
              <div v-else>
                <v-card-title>
                  <span class="text-h6">Reset Password</span>
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="newPassword"
                    label="New Password"
                    type="password"
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    dense
                  ></v-text-field>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="forgotPassword = false" color="primary" outlined>Cancel</v-btn>
                  <v-btn @click="savePassword" color="primary" depressed>Save</v-btn>
                </v-card-actions>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Panel (Optional Welcome / Image / Branding) -->
       <v-col cols="12" md="5" class="right-panel d-flex align-center justify-center hidden-sm-and-down pa-0">
          <v-img 
              :src="$config.loginImage1.path" 
              contain
              width="100%"
              :max-height="$config.loginImage1.height"
              />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
export default {
  name: 'Login',
  data() {
    return {
      //username: 'rjbetts',
      //password: 'password1',
      username: '',
      password: '',
      emailAddress: '',
      message: '',
      pinNo: '',
      pinLabel: 'Pin number',
      code: Array(6).fill(''),
      forgotPassword: false,
      emailSent: false,
      passwordResetCode: Array(6).fill(''),
      resetPassword: false,
      newPassword: '',
      confirmPassword: '',
      loginBackgroundPath: '',
    };
  },
  mounted: function () {
    this.logOut();
    this.$store.commit('setTwoFactorMode', null);
    this.loginBackgroundPath = this.$config?.loginImageBackground?.path || '';
  },
  computed: {
    twoFactorMode() {
      return this.$store.state.twoFactorMode;
    },
    useNewLoginScreen() {
      return Boolean(this.$config && this.$config.newLoginScreen);
    },
    loginContainerStyle() {
      if (!this.loginBackgroundPath) {
        return {
          backgroundColor: '#e6e6e6',
        };
      }

      return {
        backgroundImage: `url("${encodeURI(this.loginBackgroundPath)}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      };
    },
    loginImage1Path() {
      return this.$config && this.$config.loginImage1 && this.$config.loginImage1.path
        ? this.$config.loginImage1.path
        : '';
    },
    loginImage1Height() {
      return this.$config && this.$config.loginImage1 && this.$config.loginImage1.height
        ? this.$config.loginImage1.height
        : undefined;
    },
    showLoginImage1() {
      return Boolean(this.loginImage1Path);
    },
  },
  methods: {
    login() {
      const loginData = {
        userName: this.username,
        password: this.password,
      };
      this.$store.commit('setLoginData', loginData);
    },
    logOut() {
      this.$store.commit('logout');
    },
    onInput(index) {
      const val = this.code[index];
      if (val && /^[0-9]$/.test(val)) {
        // Move to next input
        if (index < this.code.length - 1) {
          this.$refs.inputs[index + 1].focus();
        }
      } else {
        // Invalid character
        this.code[index] = '';
      }
    },
    onBackspace(index) {
      if (this.code[index] === '') {
        if (index > 0) {
          this.$refs.inputs[index - 1].focus();
        }
      }
    },
    onInputs(index) {
      const val = this.passwordResetCode[index];
      if (val && /^[0-9]$/.test(val)) {
        // Move to next input
        if (index < this.passwordResetCode.length - 1) {
          this.$refs.resetInputs[index + 1].focus();
        }
      } else {
        // Invalid character
        this.passwordResetCode[index] = '';
      }
    },
    onBackspaces(index) {
      if (this.passwordResetCode[index] === '') {
        if (index > 0) {
          this.$refs.resetInputs[index - 1].focus();
        }
      }
    },
    setTwoFactorCode() {
      const code = this.code.join('');
      this.$store.commit('setTwoFactorCode', code);
    },
    async sendEmail() {
      try {
        await this.$api.post('/User/SendForgotPasswordEmail?userEmail=' + this.emailAddress);
        this.emailSent = true;
      } catch (err) {
        console.log(err);
      } finally {
        //
      }
    },
    async validateResetCode() {
      try {
        const code = this.passwordResetCode.join('');
        let response = await this.$api.get('/User/ValidateResetPasswordCode', {
          params: {
            userEmail: this.emailAddress,
            code: code,
          },
        });
        if (response) {
          this.resetPassword = true;
        }
      } catch (err) {
        console.log(err);
      } finally {
        //
      }
    },
    async savePassword() {
      try {
        const code = this.passwordResetCode.join('');
        var data = {
          UserEmail: this.emailAddress,
          NewPassword: this.newPassword,
          confirmPassword: this.confirmPassword,
          Code: code,
        };
        await this.$api.post('/user/ResetPassword', data);

        this.passwordReset = false;
        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Password is saved',
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.forgotPassword = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.login-container {
  display: flex;
  height: 100vh; /* Full viewport height */
}

.left-panel,
.right-panel {
  display: flex;
  justify-content: center;
  align-items: center;
}

.left-panel {
  background-color: #d6d6d6;
  color: white;
}

.right-panel {
  background-color: #ecf0f1;
  flex-direction: column;
}

@media (max-width: 959px) {
  .right-panel {
    display: none !important;
  }
}

.loginForm {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh; /* full viewport height */
  width: 100vw;
  background-color: #f5f5f5; /* optional: light background */
  overflow: hidden;
}

.code-inputs {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 1rem;
  margin-top: 2rem;
}
.code-inputs input {
  width: 40px;
  height: 45px;
  font-size: 24px;
  text-align: center;
  margin-right: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

input:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 5px #007bff;
}

.v-card {
  width: 90%;

  padding: 20px;
  box-sizing: border-box;
}
</style>
